import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MilkQualityTrendData } from "../hooks/useInsightsDashboardQueries";

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

type ChartRow = {
  period: number; // ms timestamp
  fat: number | null;
  protein: number | null;
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-GB", {
    month: "short",
    year: "2-digit",
  });
}

function computeMonthlyTicks(minTs: number, maxTs: number): number[] {
  const ticks: number[] = [];
  const d = new Date(minTs);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  while (d.getTime() <= maxTs) {
    ticks.push(d.getTime());
    d.setMonth(d.getMonth() + 1);
  }
  return ticks;
}

/**
 * Zoom the axis tightly around the data so small % variation is visible.
 * Uses the inter-quartile range (padded) rather than min/max so anomalous
 * readings — even a contiguous block of bad data (e.g. a stretch of 20%
 * protein) — can't blow out the scale. Quartiles are unaffected unless more
 * than a quarter of the data is anomalous.
 */
function computeDomain(rows: ChartRow[]): [number, number] | [number, string] {
  const values = rows
    .flatMap((r) => [r.fat, r.protein])
    .filter((v): v is number => v != null && v > 0)
    .sort((a, b) => a - b);
  if (values.length === 0) return [0, "auto"];
  const q = (p: number) =>
    values[Math.min(values.length - 1, Math.floor((values.length - 1) * p))];
  const lo = Math.max(0, Math.floor((q(0.25) - 0.75) * 2) / 2);
  const hi = Math.ceil((q(0.75) + 0.75) * 2) / 2;
  if (hi <= lo) return [Math.max(0, lo - 0.5), lo + 0.5];
  return [lo, hi];
}

interface Props {
  data: MilkQualityTrendData[] | undefined;
  isLoading: boolean;
}

export default function MilkQualityTrendChart({ data, isLoading }: Props) {
  const chartData = useMemo<ChartRow[]>(() => {
    if (!data) return [];
    return data
      .map((r) => ({
        period: new Date(
          String(r["MilkQuality.periodStart"]).split("T")[0]
        ).getTime(),
        fat:
          r["MilkQuality.avgFatPercent"] != null
            ? Number(r["MilkQuality.avgFatPercent"])
            : null,
        protein:
          r["MilkQuality.avgProteinPercent"] != null
            ? Number(r["MilkQuality.avgProteinPercent"])
            : null,
      }))
      .sort((a, b) => a.period - b.period);
  }, [data]);

  const domain = useMemo(() => computeDomain(chartData), [chartData]);
  const monthlyTicks = useMemo(() => {
    if (chartData.length === 0) return [];
    return computeMonthlyTicks(
      chartData[0].period,
      chartData[chartData.length - 1].period
    );
  }, [chartData]);

  if (isLoading) {
    return (
      <Card sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Fat &amp; Protein
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 0.5 }}
      >
        Fat &amp; Protein
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#71717a", mb: 1.5, display: "block" }}
      >
        Programme average composition over time
      </Typography>
      <Box sx={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 5, right: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="period"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              ticks={monthlyTicks}
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              minTickGap={36}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              axisLine={false}
              domain={domain}
              allowDataOverflow
              width={46}
              tickFormatter={(v: number) => `${v}%`}
            />
            <Tooltip
              labelFormatter={(label) =>
                new Date(Number(label)).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              }
              formatter={(value: number, name: string) => {
                const label = name === "fat" ? "Avg Fat" : "Avg Protein";
                return [
                  value.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) + "%",
                  label,
                ];
              }}
            />
            <Legend
              formatter={(value: string) => {
                const labels: Record<string, string> = {
                  fat: "Fat %",
                  protein: "Protein %",
                };
                return labels[value] ?? value;
              }}
            />
            <Line
              type="monotone"
              dataKey="fat"
              stroke={COLORS.dahlia[500]}
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="protein"
              stroke={COLORS.bellflower[500]}
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}
